<script setup lang="ts">
import { storeToRefs } from "pinia";

const indexStore = useIndexStore();
const {
  isSidebarOpened,
  isSidebarLogoOpened,
  getUserModalState,
  getUserModalText,
} = storeToRefs(indexStore);
const messageStore = useMessageStore();
const { showMessageModal } = storeToRefs(messageStore);
const route = useRoute();

const layoutWrapper = ref();
const verticalContainer = ref();
const { locale } = useI18n();
const isRtl = computed(() => locale.value === 'ar-AE');

const scrollBarWidth = ref<number | undefined>(undefined);

onMounted(() => {
  if (!verticalContainer.value) return;
  scrollBarWidth.value =
    verticalContainer.value.offsetWidth - verticalContainer.value.clientWidth;
});

watch(
  () => route.path,
  (currentPath, previousPath) => {
    verticalContainer.value?.scrollTo({ top: 0, behavior: "smooth" });
  },
);
</script>

<template>
  <div
    v-show="useAuthStore().signedIn"
    ref="layoutWrapper"
    class="layout-wrapper"
    :class="{ 'layout-rtl': isRtl }"
  >
    <MgBanners />
    <div class="banner">
      <BannerApp />
    </div>
    <logged-modal :open="getUserModalState" :text="getUserModalText" />
    <sidebar-component />
    <MessageComponent :open="showMessageModal" />
    <div
      ref="verticalContainer"
      class="vertical-container pb-4"
      data-scroll
      :class="{ 'sidebar-open': isSidebarOpened }"
    >
      <HeaderComponent :scrollBarWidth="scrollBarWidth" />
      <div>
        <slot />
        <MgPlatformSwitcherSidebar v-model:open="isSidebarLogoOpened" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.banner {
  position: absolute;
  bottom: 0.5rem;
  right: 2rem;
  z-index: 10;
}
.layout-wrapper {
  display: flex;
  height: 100vh;
  max-height: 100vh;
  width: 100vw;
  overflow-y: hidden;

  &.layout-rtl {
    #sidebar {
      order: 2;
    }

    .vertical-container {
      order: 1;
    }
  }
  .vertical-container {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    height: 100vh;
    max-height: 100vh;
    > div {
      overflow: auto;
      display: flex;
      flex-direction: column;
      height: 100%;
      &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
        display: none;
      }

      &::-webkit-scrollbar-button {
        width: 0px;
        height: 0px;
      }

      &::-webkit-scrollbar-thumb {
        background: #808080;
        border: 15px none #ffffff;
        border-radius: 0px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background: #23c0e9;
      }

      &::-webkit-scrollbar-thumb:active {
        background: var(--inactive);
      }

      &::-webkit-scrollbar-track {
        background: var(--body-bg);
        border: 8px none #ffffff;
        border-radius: 0px;
      }

      &::-webkit-scrollbar-track:hover {
        background: var(--body-bg);
      }

      &::-webkit-scrollbar-track:active {
        background: var(--body-bg-2);
      }

      &::-webkit-scrollbar-corner {
        background: transparent;
      }
    }
  }
}

@media screen and (max-width: 992px) {
  body.sidebar-openned .layout-wrapper .vertical-container {
    filter: brightness(65%);
  }
}

@media screen and (max-width: 576px) {
  .layout-wrapper .vertical-container {
    transition: background-color 0.25s ease-in-out;
    overflow-x: hidden;
  }

  body.sidebar-openned .vertical-container {
    background: rgba(0, 0, 0, 0.35);
  }

  #sidebar {
    height: 100vh;
  }
}
</style>
